import * as React from 'react';
import { useIntl } from 'react-intl';
import { CmdLoader } from '@commander-services/gui-components';
import dayjs from 'dayjs';
import { useRecoilValue } from 'recoil';
import { IDetailWaypointResponseData } from './types';
import WaypointIconService from './WaypointIconService';
import { waypointDataAtom, waypointIdAtom } from './WaypointState';
import { handleAreaTypeValue } from './WaypointService';

interface IWaypointDetailForm {
  closeDetail: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  id: number | null;
}

function WaypointDetailForm(props: IWaypointDetailForm): JSX.Element {
  const { formatMessage: f, locale } = useIntl();
  const waypointData = useRecoilValue<IDetailWaypointResponseData | null>(waypointDataAtom);
  const waypointId = useRecoilValue<number | null>(waypointIdAtom);

  React.useEffect(() => {
    if (waypointId !== props.id) {
      props.closeDetail();
    }
  }, [props, waypointId]);

  const handleAreaTypeIcon = (type: string) => {
    if (type === 'commercial') {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M0.75375,16.08 C0.33768,16.08 0,15.7473451 0,15.3374668 C0,14.9275884 0.33768,14.5949336 0.75375,14.5949336 L1.005,14.594 L1.005,3.61732239 C1.005,2.92033121 1.426095,2.29363317 2.07834,2.02236103 L6.60084,0.137316691 C7.14555,-0.0894034529 7.76463,-0.032970928 8.25708,0.288793468 C8.750535,0.610557865 9.043995,1.15112205 9.043995,1.7332681 L9.043995,3.45693522 C9.043995,3.86681356 8.706315,4.19946844 8.290245,4.19946844 C7.874175,4.19946844 7.536495,3.86681356 7.536495,3.45693522 L7.536495,1.7332681 C7.536495,1.61743291 7.466145,1.55407008 7.423935,1.52634884 C7.381725,1.4986276 7.295295,1.45902583 7.186755,1.50555791 L2.664255,3.38961221 C2.571795,3.42822393 2.511495,3.51732792 2.511495,3.61732239 L2.511,14.594 L6.03,14.5949336 L6.03,6.92209026 C6.03,5.96669751 6.818925,5.18951274 7.78875,5.18951274 L13.31625,5.18951274 C14.286075,5.18951274 15.075,5.96669751 15.075,6.92209026 L15.075,14.5949336 L15.32625,14.5949336 C15.74232,14.5949336 16.08,14.9275884 16.08,15.3374668 C16.08,15.7473451 15.74232,16.08 15.32625,16.08 Z M9.29625,11.1297785 C8.88018,11.1297785 8.5425,11.4624334 8.5425,11.8723117 L8.5425,12.3673339 C8.5425,12.7772122 8.88018,13.1098671 9.29625,13.1098671 C9.71232,13.1098671 10.05,12.7772122 10.05,12.3673339 L10.05,11.8723117 C10.05,11.4624334 9.71232,11.1297785 9.29625,11.1297785 Z M11.80875,11.1297785 C11.39268,11.1297785 11.055,11.4624334 11.055,11.8723117 L11.055,12.3673339 C11.055,12.7772122 11.39268,13.1098671 11.80875,13.1098671 C12.22482,13.1098671 12.5625,12.7772122 12.5625,12.3673339 L12.5625,11.8723117 C12.5625,11.4624334 12.22482,11.1297785 11.80875,11.1297785 Z M9.29625,8.15964563 C8.88018,8.15964563 8.5425,8.49230051 8.5425,8.90217885 L8.5425,9.397201 C8.5425,9.80707934 8.88018,10.1397342 9.29625,10.1397342 C9.71232,10.1397342 10.05,9.80707934 10.05,9.397201 L10.05,8.90217885 C10.05,8.49230051 9.71232,8.15964563 9.29625,8.15964563 Z M11.80875,8.15964563 C11.39268,8.15964563 11.055,8.49230051 11.055,8.90217885 L11.055,9.397201 C11.055,9.80707934 11.39268,10.1397342 11.80875,10.1397342 C12.22482,10.1397342 12.5625,9.80707934 12.5625,9.397201 L12.5625,8.90217885 C12.5625,8.49230051 12.22482,8.15964563 11.80875,8.15964563 Z"
            fill="#737373"
          />
        </svg>
      );
    }
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M8.04,4.68557217 L14.7394556,8.74472348 L14.7394556,13.6330435 C14.7394556,14.9823652 13.6876411,16.08 12.3946461,16.08 L9.04491834,16.08 L9.04491834,12.933913 C9.04491834,12.355033 8.59471492,11.8852174 8.04,11.8852174 C7.48528508,11.8852174 7.03508166,12.355033 7.03508166,12.933913 L7.03508166,16.08 L3.68535386,16.08 C2.39235892,16.08 1.34054439,14.9823652 1.34054439,13.6330435 L1.34054439,8.74472348 L8.04,4.68697043 Z M8.54513895,0.142622609 L15.5795673,4.40452174 C16.0592484,4.69536 16.2227151,5.33716174 15.9440177,5.83773913 C15.7577729,6.17332174 15.4201203,6.3606887 15.0744284,6.3606887 C14.9029223,6.35929043 14.7300764,6.31314783 14.5706293,6.21666783 L8.04,2.26098783 L1.50937067,6.21666783 C1.03236944,6.50750609 0.414679629,6.33691826 0.135982275,5.83634087 C-0.142715078,5.33576348 0.0207516391,4.69396174 0.50043266,4.40312348 L7.53620094,0.142622609 C7.84705568,-0.0475408696 8.23428421,-0.0475408696 8.54513895,0.142622609 Z"
          fill="#737373"
        />
      </svg>
    );
  };

  return (
    <div className="b-mc-waypoints">
      {!waypointData && <CmdLoader inContent={true} />}
      <div className="b-mc-waypoints-title">
        <a href="#">
          <i className="cmd-icon cmd-icon-back mr-1" onClick={props.closeDetail} />
          {f({ id: 'table.detail' })}
        </a>
      </div>
      <div className="b-waypoints-detail h-[300px] overflow-y-auto text-sm text-[#737373] ">
        <div className="item">
          <div className="mb-1 font-semibold">{f({ id: 'enum.area.tab.title' })}</div>
          <div>{waypointData && f({ id: handleAreaTypeValue(waypointData).item })}</div>
        </div>

        <div className="item">
          <div className="mb-1 font-semibold">{f({ id: 'waypoints.form.customer.title' })}</div>
          <div>
            {waypointData?.customerName && waypointData.customerLabel
              ? `${waypointData.customerName} (${waypointData.customerLabel})`
              : ''}
          </div>
        </div>

        <div className="item">
          <div className="mb-1 font-semibold">{f({ id: 'waypoints.form.category.title' })}</div>
          <div className="flex items-center gap-2">
            <i
              className={`cmd-icon cmd-icon-${WaypointIconService.getCssIcon(
                waypointData?.category?.icon || 'default'
              )}`}
            />
            {waypointData?.category?.name || f({ id: 'waypoints.form.customer.title' })}
          </div>
        </div>

        <div className="item">
          <div className="mb-1 font-semibold">{f({ id: 'waypoints.detail.item.title' })}</div>
          <div>{waypointData?.name}</div>
        </div>

        <div className="item">
          <div className="mb-1 font-semibold">{f({ id: 'waypoints.detail.item.type' })}</div>
          <div>
            {waypointData?.type === 'private'
              ? f({ id: 'waypoints.detail.item.private' })
              : f({ id: 'waypoints.detail.item.customer' })}
          </div>
        </div>

        {waypointData?.refueling?.type && (
          <div className="item">
            <div className="mb-1 font-semibold">
              {f({ id: 'enum.area.refuelingTypeList.title' })}
            </div>
            <div>
              <div className="flex gap-2">
                {handleAreaTypeIcon(waypointData.refueling.type)}
                {waypointData.refueling.type === 'private'
                  ? f({ id: 'enum.waypoints.refueling.type.private' })
                  : f({ id: 'enum.waypoints.refueling.type.commercial' })}
              </div>
            </div>
          </div>
        )}

        {waypointData?.refueling && (
          <div className="item">
            <div className="mb-1 font-semibold">{f({ id: 'enum.area.detail.typeAndPrice' })}</div>
            {waypointData.refueling.fuels.map((fuel, i) => (
              <div key={i} className="flex justify-between">
                <span>{fuel.name}</span>
                <span>
                  {new Intl.NumberFormat(locale, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 3,
                  }).format(fuel.unitPriceIncVat)}{' '}
                  {fuel.currency}/{fuel.unit}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="item">
          <div className="mb-1 font-semibold">{f({ id: 'table_list.columns.createdBy' })}</div>
          <div>
            {waypointData?.createdByName}
            {waypointData?.createdAt && (
              <>
                {' '}
                – {dayjs(waypointData.createdAt).format('DD.MM.YYYY')},{' '}
                {dayjs(waypointData.createdAt).format('HH:mm')}
              </>
            )}
          </div>
        </div>

        <div className="item">
          <div className="mb-1 font-semibold">{f({ id: 'table_list.columns.modifiedBy' })}</div>
          <div>
            {waypointData?.modifiedByName}
            {waypointData?.modifiedAt && (
              <>
                {' '}
                – {dayjs(waypointData.modifiedAt).format('DD.MM.YYYY')},{' '}
                {dayjs(waypointData.modifiedAt).format('HH:mm')}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="b-mc-waypoints-footer">
        <div className="row">
          <div className="col">
            <a href="#" className="e-button e-button--gray" onClick={props.closeDetail}>
              {f({ id: 'waypoints.detail.cancel' })}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WaypointDetailForm;
